import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useForm, z, zodResolver } from "@/hooks/schema";
import { Form } from "@/components/Form";
import { Check, Pencil } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { getShowProducts, updateProductsData } from "@/Api/products";
import { useToastNavigate } from "@/utils/toastNavigate";
import { LoaderSpin, CreateLoaderInput } from "@/components/Loaders";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Mask } from "@/utils/mask";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getSelectProductGroups } from "@/Api/productGroups";

const schema = z.object({
  game_quantity: z.string({ message: "*requerido" }).min(1, "*requerido"),
  dez_quantity: z.string({ message: "*requerido" }).min(1, "*requerido"),
  quota_quantity: z.string({ message: "*requerido" }).min(1, "*requerido"),
  total_without_tariff: z
    .string({ message: "*requerido" })
    .min(1, "*requerido"),
  total_with_tariff: z.string({ message: "*requerido" }).min(1, "*requerido"),
  game_percentage: z.string({ message: "*requerido" }).min(1, "*requerido"),
  tariff: z.string({ message: "*requerido" }).min(1, "*requerido"),
  sale_price: z.string({ message: "*requerido" }).min(1, "*requerido"),
  price_without_tariff: z
    .string({ message: "*requerido" })
    .min(1, "*requerido"),
  commission_by_quote: z.string({ message: "*requerido" }).min(1, "*requerido"),
  product_type_id: z.string({ message: "*requerido" }).min(1, "*requerido"),
  lottery_id: z.string({ message: "*requerido" }).min(1, "*requerido"),
});

type SchemaType = z.infer<typeof schema>;

export const UpdateBtn = ({
  updateFront,
  uuid,
}: {
  updateFront: (response: any) => void;
  uuid: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [lotteries, setLotteries] = useState([]);
  const inputRefDez = useRef<HTMLInputElement>(null);

  const form = useForm<SchemaType>({
    resolver: zodResolver(schema),
  });

  const { toast } = useToastNavigate();
  const [formState, setFormState] = useState<string>("default");

  const quotaQuantity = form.watch("quota_quantity");
  const totalWithoutTariff = form.watch("total_without_tariff");

  useEffect(() => {
    const fetchLotteries = async () => {
      const { data: lotteriesData } = await getSelectProductGroups();
      setLotteries(lotteriesData?.response || []);
    };
    if (isOpen) {
      fetchLotteries();
    }
  }, [isOpen]);

  useEffect(() => {
    if (quotaQuantity && totalWithoutTariff) {
      const total = Number(Mask.transformCurrencyInFloat(totalWithoutTariff));
      const quotas = Number(quotaQuantity);

      const tariff = total * 0.35;
      form.setValue("tariff", Mask.formatCurrency(tariff));

      const totalWithTariff = total + tariff;
      form.setValue("total_with_tariff", Mask.formatCurrency(totalWithTariff));

      const priceWithoutTariff = total / quotas;
      form.setValue(
        "price_without_tariff",
        Mask.formatCurrency(priceWithoutTariff)
      );

      const salePrice = totalWithTariff / quotas;
      form.setValue("sale_price", Mask.formatCurrency(salePrice));

      const commission = tariff / quotas;
      form.setValue("commission_by_quote", commission.toFixed(2));
    }
  }, [quotaQuantity, totalWithoutTariff]);

  useEffect(() => {
    const fetchData = async () => {
      if (isOpen) {
        setIsLoading(true);
        try {
          const [{ data: responseApi }] = await Promise.all([
            getShowProducts(uuid),
          ]);

          const response = responseApi?.response;
          if (response) {
            form.setValue(
              "game_quantity",
              String(response.game_quantity) || ""
            );
            form.setValue("dez_quantity", String(response.dez_quantity) || "");
            form.setValue(
              "quota_quantity",
              String(response.quota_quantity) || ""
            );
            form.setValue(
              "total_without_tariff",
              Mask.formatCurrency(response.total_without_tariff) || ""
            );
            form.setValue(
              "total_with_tariff",
              Mask.formatCurrency(response.total_with_tariff) || ""
            );
            form.setValue(
              "game_percentage",
              String(response.game_percentage) || ""
            );
            form.setValue("tariff", Mask.formatCurrency(response.tariff) || "");
            form.setValue(
              "sale_price",
              Mask.formatCurrency(response.sale_price) || ""
            );
            form.setValue(
              "price_without_tariff",
              Mask.formatCurrency(response.price_without_tariff) || ""
            );
            form.setValue(
              "commission_by_quote",
              String(response.commission_by_quote) || ""
            );
            form.setValue(
              "product_type_id",
              String(response.product_type_id) || ""
            );
            form.setValue("lottery_id", String(response.lottery_id) || "");

            if (response.lottery_id === 3) {
              inputRefDez.current &&
                (inputRefDez.current.placeholder =
                  "Ex: 15, 16, 17, 18, 19, 20");
            }
          }
        } catch (error) {
          console.error(error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchData();
  }, [isOpen, uuid, form]);

  const formSubmit = async (data: SchemaType) => {
    setFormState("loading");
    if (data.lottery_id === "3") {
      const dezQuantity = eval(data?.dez_quantity);

      if (dezQuantity < 15 || dezQuantity > 20) {
        toast({
          description: "A quantidade de dezenas deve ser entre 15 e 20",
        });
        setFormState("default");
        return;
      }
    }
    const payload = {
      ...data,
      game_quantity: Number(data.game_quantity),
      dez_quantity: String(data.dez_quantity),
      quota_quantity: Number(data.quota_quantity),
      total_without_tariff: Number(
        Mask.transformCurrencyInFloat(data.total_without_tariff)
      ),
      total_with_tariff: Number(
        Mask.transformCurrencyInFloat(data.total_with_tariff)
      ),
      game_percentage: Number(data.game_percentage),
      tariff: Number(Mask.transformCurrencyInFloat(data.tariff)),
      sale_price: Number(Mask.transformCurrencyInFloat(data.sale_price)),
      price_without_tariff: Number(
        Mask.transformCurrencyInFloat(data.price_without_tariff)
      ),
      commission_by_quote: Number(data.commission_by_quote),
      product_type_id: Number(data.product_type_id),
      lottery_id: Number(data.lottery_id),
    };

    const { data: apiResponse, ok } = await updateProductsData(uuid, payload);
    if (!ok) {
      setFormState("error");
      setTimeout(() => {
        setFormState("default");
      }, 1000);
      setIsOpen(false);
      return;
    }

    updateFront(apiResponse?.response);
    setFormState("default");
    setIsOpen(false);
    toast({
      description: apiResponse?.message,
      action: <Check color="#1edc2b" />,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="size-7 p-0 text-slate-900 hover:bg-stone-950/10 dark:text-[#F4F4F5]"
          onClick={() => setIsOpen(true)}
        >
          <Pencil className="size-5" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[800px] h-auto">
        <DialogHeader className="flex flex-col gap-2">
          <DialogDescription className="text-lg">
            Editar Produto
          </DialogDescription>
          <DialogTitle className="text-2xl">Informações Básicas</DialogTitle>
        </DialogHeader>
        {isLoading ? (
          <CreateLoaderInput lines={10} />
        ) : (
          <ScrollArea className="max-h-[80vh]">
            <Form
              onSubmit={form.handleSubmit(formSubmit)}
              {...form}
              className="space-y-3"
            >
              <div className="grid grid-cols-2 gap-4">
                <Form.Row>
                  <Form.Field name="lottery_id">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>
                          Loteria <Form.Message />
                        </Form.Label>
                        <Select
                          onValueChange={(value) => {
                            field.onChange(value);
                            if (value === "3") {
                              inputRefDez.current &&
                                (inputRefDez.current.placeholder =
                                  "Ex: 15, 16, 17, 18, 19, 20");
                            } else {
                              inputRefDez.current &&
                                (inputRefDez.current.placeholder = "Ex: 11");
                            }
                          }}
                          value={field.value}
                        >
                          <Form.Control>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione uma loteria" />
                            </SelectTrigger>
                          </Form.Control>
                          <SelectContent>
                            {lotteries.map((lottery: any) => (
                              <SelectItem
                                key={lottery.id}
                                value={lottery.id.toString()}
                              >
                                {lottery.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>

                <Form.Row>
                  <Form.Field name="game_quantity">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>
                          Quantidade de Jogos <Form.Message />
                        </Form.Label>
                        <Form.Control>
                          <Input {...field} type="number" placeholder="Ex: 5" />
                        </Form.Control>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Form.Row>
                  <Form.Field name="dez_quantity">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>
                          Quantidade de Dezenas <Form.Message />
                        </Form.Label>
                        <Form.Control>
                          <Input
                            {...field}
                            placeholder="Ex: 11"
                            ref={inputRefDez}
                          />
                        </Form.Control>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>

                <Form.Row>
                  <Form.Field name="quota_quantity">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>
                          Quantidade de Cotas <Form.Message />
                        </Form.Label>
                        <Form.Control>
                          <Input
                            {...field}
                            type="number"
                            placeholder="Ex: 23"
                          />
                        </Form.Control>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>
              </div>

              <div className="border-t pt-4 mt-4">
                <h3 className="text-lg font-medium mb-4">Cálculo do Preço</h3>
                <Form.Row>
                  <Form.Field name="total_without_tariff">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>
                          Total sem Tarifa <Form.Message />
                        </Form.Label>
                        <Form.Control>
                          <Input
                            {...field}
                            typeMask="currency"
                            placeholder="R$ 0,00"
                          />
                        </Form.Control>
                      </Form.Item>
                    )}
                  </Form.Field>

                  <Form.Field name="tariff">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>
                          Tarifa <Form.Message />
                        </Form.Label>
                        <Form.Control>
                          <Input
                            {...field}
                            typeMask="currency"
                            placeholder="R$ 0,00"
                          />
                        </Form.Control>
                      </Form.Item>
                    )}
                  </Form.Field>

                  <Form.Field name="total_with_tariff">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>
                          Total com Tarifa <Form.Message />
                        </Form.Label>
                        <Form.Control>
                          <Input
                            {...field}
                            typeMask="currency"
                            placeholder="R$ 0,00"
                          />
                        </Form.Control>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>

                <div className="mt-3">
                  <Form.Row>
                    <Form.Field name="sale_price">
                      {({ field }) => (
                        <Form.Item>
                          <Form.Label>
                            Preço de Venda <Form.Message />
                          </Form.Label>
                          <Form.Control>
                            <Input
                              {...field}
                              typeMask="currency"
                              placeholder="R$ 0,00"
                            />
                          </Form.Control>
                        </Form.Item>
                      )}
                    </Form.Field>

                    <Form.Field name="price_without_tariff">
                      {({ field }) => (
                        <Form.Item>
                          <Form.Label>
                            Preço sem Tarifa <Form.Message />
                          </Form.Label>
                          <Form.Control>
                            <Input
                              {...field}
                              typeMask="currency"
                              placeholder="R$ 0,00"
                            />
                          </Form.Control>
                        </Form.Item>
                      )}
                    </Form.Field>

                    <Form.Field name="commission_by_quote">
                      {({ field }) => (
                        <Form.Item>
                          <Form.Label>
                            Comissão por Cota <Form.Message />
                          </Form.Label>
                          <Form.Control>
                            <Input
                              {...field}
                              type="number"
                              placeholder="Ex: 76.08"
                            />
                          </Form.Control>
                        </Form.Item>
                      )}
                    </Form.Field>
                  </Form.Row>
                </div>
              </div>

              <DialogFooter>
                <Button
                  type="submit"
                  className={cn("w-full", colors.bg_primary)}
                  disabled={formState !== "default"}
                >
                  {formState === "loading" && (
                    <>
                      Atualizando <LoaderSpin />
                    </>
                  )}
                  {formState === "default" && "Atualizar"}
                  {formState === "error" && "Erro ao atualizar"}
                </Button>
              </DialogFooter>
            </Form>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
};
